import { useEffect } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { comics, categories } from '@/mocks/comics';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const comic = comics.find(c => c.id === Number(id));

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location]);

  useEffect(() => {
    if (comic) {
      document.title = `${comic.title} - 基德漫画网免费在线观看下拉式`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', `${comic.title}，${comic.description?.slice(0, 120)}... 基德漫画网提供${comic.title}免费在线观看下拉式，高清画质、流畅阅读。`);
    }
  }, [comic]);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-foreground-400 mb-4">
              <i className="ri-file-unknow-line text-3xl"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">该漫画不存在或已被移除</p>
            <Link to="/category/all" className="inline-flex px-6 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium whitespace-nowrap cursor-pointer transition-colors duration-200">
              返回漫画库
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const comicCategories = comic.categories.map(slug => categories.find(c => c.slug === slug)).filter(Boolean);
  const relatedComics = comics
    .filter(c => c.id !== comic.id && c.categories.some(cat => comic.categories.includes(cat)))
    .slice(0, 5);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: comic.title,
              author: { '@type': 'Person', name: comic.author },
              datePublished: comic.chapters[0]?.date,
              dateModified: comic.updateDate,
              description: comic.description,
              image: comic.cover,
              breadcrumb: {
                '@type': 'BreadcrumbList',
                itemListElement: [
                  { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.jidemanhua.com/' },
                  { '@type': 'ListItem', position: 2, name: '漫画详情', item: `https://www.jidemanhua.com/article/${comic.id}` },
                ],
              },
            }),
          }}
        />

        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: '全部漫画', to: '/category/all' },
            { label: comic.title },
          ]}
        />

        <section className="w-full px-4 md:px-6 py-6">
          <div className="max-w-[1280px] mx-auto">
            <div className="flex flex-col md:flex-row gap-8 md:gap-10">
              <div className="flex-shrink-0 w-full md:w-[280px]">
                <div className="rounded-lg overflow-hidden bg-background-100">
                  <div className="aspect-[3/4]">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {comicCategories.map((cat) => cat && (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      className="px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-foreground-600 hover:bg-secondary-200 whitespace-nowrap cursor-pointer transition-colors duration-200"
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-4 mt-4">
                  <span className="text-sm text-foreground-600">
                    <strong className="text-foreground-800">作者：</strong>{comic.author}
                  </span>
                  <span className="flex items-center gap-1 text-sm">
                    <i className="ri-star-fill text-primary-500"></i>
                    <strong className="text-foreground-800">{comic.rating}</strong>
                    <span className="text-foreground-500">分</span>
                  </span>
                  <span className="text-sm text-foreground-500">
                    <i className="ri-eye-line mr-1"></i>
                    {(comic.views / 10000).toFixed(0)}万次阅读
                  </span>
                  <span className={`px-2.5 py-0.5 rounded-md text-xs font-medium whitespace-nowrap ${
                    comic.status === '连载中' ? 'bg-accent-100 text-accent-700' : 'bg-secondary-200 text-secondary-700'
                  }`}>
                    {comic.status}
                  </span>
                </div>

                <div className="mt-6 p-5 rounded-lg bg-background-100 border border-secondary-200/60">
                  <h2 className="text-sm font-semibold text-foreground-800 mb-2">作品简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>

                <div className="mt-8">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-heading text-lg font-bold text-foreground-950">章节列表</h2>
                    <span className="text-xs text-foreground-500">共 {comic.chapters.length} 话</span>
                  </div>
                  <div className="border border-secondary-200/60 rounded-lg divide-y divide-secondary-200/60">
                    {comic.chapters.map((ch, idx) => (
                      <a
                        key={ch.num}
                        href={`#chapter-${ch.num}`}
                        className="flex items-center justify-between px-5 py-3.5 hover:bg-primary-50/50 cursor-pointer transition-colors duration-200 group"
                      >
                        <div className="flex items-center gap-3">
                          <span className="w-7 h-7 flex items-center justify-center rounded-md bg-secondary-100 text-foreground-500 text-xs font-medium group-hover:bg-primary-100 group-hover:text-primary-600 transition-colors duration-200">
                            {ch.num}
                          </span>
                          <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors duration-200">
                            {ch.title}
                          </span>
                        </div>
                        <span className="text-xs text-foreground-400">{ch.date}</span>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {relatedComics.length > 0 && (
              <div className="mt-16 pt-10 border-t border-secondary-200/60">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading text-xl font-bold text-foreground-950">相关推荐</h2>
                  <Link to="/category/all" className="text-sm text-primary-600 hover:text-primary-700 whitespace-nowrap cursor-pointer transition-colors duration-200 font-medium">
                    查看更多 <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop>
                  {relatedComics.map((rc) => (
                    <ComicCard
                      key={rc.id}
                      id={rc.id}
                      title={rc.title}
                      author={rc.author}
                      cover={rc.cover}
                      rating={rc.rating}
                      status={rc.status}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}